import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { APP_COLORS as C, APP_FONT as F } from './tokens';
import AppHeader from './AppHeader';
import Sidebar from '../Sidebar';
import { useAppLayoutData } from './useAppLayoutData';
import { getInitials } from './helpers';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { MobileHamburger } from '../MobileHamburger';
import type { AppUser } from './types';

type Props = {
  children?: React.ReactNode;
  activeNavId?: string;
  panel?: React.ReactNode;
  pageHeader?: React.ReactNode;
};

const FALLBACK_USER: AppUser = {
  id: 'local',
  name: 'Guest',
  email: '',
  initials: 'G',
  avatarUrl: null,
  role: 'owner',
  companyName: 'Novala',
};

const FULL_BLEED_MAIN_PATHS = new Set<string>([
  '/accounting/reconcile/session',
  '/accounting/recurring',
]);

const FULL_BLEED_MAIN_PATTERNS: RegExp[] = [
  /^\/accounting\/chart-of-accounts\/[^/]+\/report$/,
];

// Pages that opt in to a flush top: main keeps horizontal and bottom padding
// but drops the top padding so the page body sits snug under the pageHeader
// slot. Use for pages whose pageHeader is just a back link + breadcrumb.
const FLUSH_TOP_MAIN_PATHS = new Set<string>([
  '/accounting/reports/account-list',
  '/accounting/chart-of-accounts/create-multiple',
  '/accounting/chart-of-accounts/review-archive',
  '/accounting/chart-of-accounts/review-merge',
  '/accounting/chart-of-accounts/review-categorize',
]);

function userFromLocalStorage(): AppUser | null {
  try {
    const email = localStorage.getItem('user_email') || '';
    const fullName = localStorage.getItem('full_name') || localStorage.getItem('user_name') || '';
    const firstName = localStorage.getItem('first_name') || '';
    const displayName = fullName || firstName || (email ? email.split('@')[0] : '');
    if (!displayName && !email) return null;
    const name = displayName || email;
    return {
      id: 'local',
      name,
      email,
      initials: name.trim().charAt(0).toUpperCase(),
      avatarUrl: null,
      role: 'owner',
      companyName: 'Novala',
    };
  } catch (e) {
    return null;
  }
}

const SidebarAny = Sidebar as unknown as React.ComponentType<{
  onLogout?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktop?: boolean;
}>;

export default function AppLayout({ children, activeNavId, panel, pageHeader }: Props): React.ReactElement {
  const { data, isLoading } = useAppLayoutData();
  const location = useLocation();
  const user = data?.user ?? userFromLocalStorage() ?? FALLBACK_USER;

  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const handleLogout = () => { localStorage.clear(); window.location.href = '/login'; };
  const handleVelaClick = () => { window.location.href = '/agents'; };

  const isFullBleed =
    FULL_BLEED_MAIN_PATHS.has(location.pathname) ||
    FULL_BLEED_MAIN_PATTERNS.some((p) => p.test(location.pathname));
  const isFlushTop = FLUSH_TOP_MAIN_PATHS.has(location.pathname);
  const mainPadding = isFullBleed
    ? 0
    : isMobile
      ? (isFlushTop ? '0 16px 60px' : '16px 16px 60px')
      : (isFlushTop ? '0 32px 90px' : '28px 32px 90px');

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      background: '#F4F6F8',
      fontFamily: F.family,
      color: C.ink,
      display: 'flex',
      flexDirection: 'column',
      padding: isMobile ? '8px' : '14px',
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        background: '#F7F8FA',
        borderRadius: isMobile ? 14 : 20,
        overflow: 'hidden',
        border: '1px solid #E7EAF0',
        boxShadow: '0 2px 6px rgba(14,26,26,0.05), 0 10px 24px rgba(14,26,26,0.05)',
        minHeight: 0,
        position: 'relative',
      }}>
        <SidebarAny
          onLogout={handleLogout}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
          isMobile={isMobile}
          isTablet={isTablet}
          isDesktop={isDesktop}
        />
        {!isMobile && panel}
        {mobileMenuOpen && isMobile && (
          <div onClick={() => setMobileMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 39, backdropFilter: 'blur(2px)' }} />
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, padding: isMobile ? 0 : '10px 10px 10px 0' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: isMobile ? 10 : 16, border: '1px solid #E7EAF0', boxShadow: '0 1px 3px rgba(14,26,26,0.04)', overflow: 'hidden', minHeight: 0 }}>
            {isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: '1px solid #E7EAF0', background: '#fff', flexShrink: 0 }}>
                <MobileHamburger isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} />
                <img src="/apple-touch-icon.png" alt="Novala" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
              </div>
            )}
            <AppHeader
              user={user}
              unreadInbox={data?.unreadInbox ?? 0}
              unreadNotifications={data?.unreadNotifications ?? 0}
              onLogout={handleLogout}
              onVelaClick={handleVelaClick}
            />
            {pageHeader}
            <main style={{ flex: 1, minWidth: 0, minHeight: 0, maxWidth: '100%', margin: 0, padding: mainPadding, overflowY: 'auto', overflowX: 'hidden' }}>
              {isLoading && !data ? (
                <div style={{ color: C.sub, fontSize: 13, padding: '40px 0' }}>Loading your workspace...</div>
              ) : (children ?? <Outlet />)}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}